import React, { useEffect, useState } from 'react';
import DashCollapse from 'components/_UI/DashCollapse';
import ProductForm from './create/ProductForm';
import routeParams from 'theme/routeParams';
import { useSearchParams } from 'react-router-dom';
import { Column } from 'styled/alignment/Column';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import { useUser } from 'context/user/User';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import Tabs from 'components/_UI/Tabs';
import { Calendar, Package, Store } from 'lucide-react';
import roles from 'helpers/roles';
import TemplateProductsList from './list/TemplateProductsList';
import EventsProductsList from './list/EventsProductsList';
import EstablishmentsProductsList from './list/EstablishmentsProductsList';
import DeleteProductModal from './modals/DeleteProductModal';
import NoEstablishmentModal from './modals/NoEstablishmentModal';

export default function Products({ setIsCreate, isCreate }) {
    const { toast, toasts } = useToast()
    const { user } = useUser()
    const [searchParams, setSearchParams] = useSearchParams();
    const showCreate = searchParams.get(routeParams.products.create);
    const isProducer = user?.role?.id == roles.PRODUCER;

    const tabs = [
        {
            name: 'template',
            label: 'Geral',
            icon: Package,
            disabled: false,
        },
        {
            name: 'event',
            label: 'A venda em eventos',
            icon: Calendar,
            disabled: false,
        },
        ...(!isProducer ? [{
            name: 'establishment',
            label: 'A venda no estabelecimento',
            icon: Store,
            disabled: user?.establishments?.length === 0,
        }] : []),
    ]

    const [activeTab, setActiveTab] = useState(tabs[0].name)
    const [noEstablishmentModal, setNoEstablishmentModal] = useState(null)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [refreshKey, setRefreshKey] = useState({ action: null, key: 0 })
    const successMessages = {
        create: 'Produto criado com sucesso!',
        edit: 'Produto editado com sucesso!',
        delete: 'Produto excluído com sucesso!',
        sell_establishment: 'Produto publicado no estabelecimento!'
    }

    useEffect(() => {
        setIsCreate(showCreate)
    }, []);

    const onChangeTab = (tab) => {
        setActiveTab(tab)
    }

    const closeCollapse = () => {
        setIsCreate(false)
        setSelectedProduct(null)
        setSearchParams((prevSearchParams) => {
            const newSearchParams = new URLSearchParams(prevSearchParams);

            Object.values(routeParams.products).map(el => {
                newSearchParams.delete(el)
            })

            return newSearchParams;
        });
    }

    const activeActionClicked = (item) => {
        setIsCreate(false)
        setSelectedProduct(item)
    }

    const createProduct = () => {
        const isEstablishment = user?.role?.id == import.meta.env.VITE_ESTABLISHMENT_ROLE_ID
        if (isEstablishment && !user?.establishments.length) {
            setNoEstablishmentModal(true)
            return
        }
        setIsCreate(true)
    }

    const handleSuccess = ({ action }) => {
        setRefreshKey(prev => ({ action, key: prev.key + 1 }))
        toast.success(successMessages[action],
            { position: 'top-center' })
    }

    const handleError = () => {
        toast.error('Ocorreu um erro inesperado', {
            position: 'top-center'
        })
    }

    const isProductFormVisible = () => {
        return (
            isCreate ||
            (
                selectedProduct &&
                (
                    selectedProduct.action === 'edit' ||
                    selectedProduct.action === 'sell_establishment'
                )
            )
        )
    }

    return (
        <>
            <ToastContainer toasts={toasts} />
            <ProductsList>
                <Tabs
                    tabs={tabs}
                    containerStyle={{ width: '100%', marginBottom: '1rem' }}
                    onChangeTab={onChangeTab}
                />
                {
                    activeTab === 'template' &&
                        <TemplateProductsList
                            onProductClick={setSelectedProduct}
                            onDropdownAction={activeActionClicked}
                            createProduct={createProduct}
                            refreshKey={refreshKey}
                        />
                }

                {
                    activeTab === 'event' &&
                        <EventsProductsList
                            onDropdownAction={activeActionClicked}
                            refreshKey={refreshKey}
                        />
                }

                {
                    activeTab === 'establishment' &&
                        <EstablishmentsProductsList
                            onProductClick={setSelectedProduct}
                            onDropdownAction={activeActionClicked}
                            createProduct={createProduct}
                            refreshKey={refreshKey}
                        />
                }
            </ProductsList>

            <DashCollapse
                visible={isProductFormVisible()}
                onClose={closeCollapse}
            >
                {
                    isProductFormVisible() &&
                        <ProductForm
                            selectedProduct={selectedProduct}
                            activeTab={activeTab}
                            onClose={closeCollapse}
                            onSuccess={(action) => handleSuccess({ action })}
                            onError={() => toast.error('Ocorreu um erro inesperado', { position: 'top-center' })}
                        />
                }
            </DashCollapse>

            {
                noEstablishmentModal &&
                    <NoEstablishmentModal
                        isOpen={!!noEstablishmentModal}
                        onClose={() => setNoEstablishmentModal(false)}
                    />
            }

            {
                selectedProduct && selectedProduct.action === 'delete' &&
                    <DeleteProductModal
                        isOpen={!!selectedProduct}
                        onClose={() => setSelectedProduct(null)}
                        onSuccess={() => handleSuccess({ action: 'delete' })}
                        onError={handleError}
                        selectedProduct={selectedProduct}
                        activeTab={activeTab}
                    />
            }
        </>
    );
}

const ProductsList = styled(Column)`
    width: 100%;
    @media screen and (min-width: ${breakpoints.sm}){
        flex-direction: row;
        flex-wrap: wrap;
    }
`